import {
  getUserUtilityCosts,
  getPlantUtilityCosts,
  updateUserUtilityCosts,
  updatePlantUtilityCosts,
} from "../../../network/cipAPI";
import {
  PENDING_PLANT_UTILITY_COSTS_KEY,
  UTILITY_FIELD_KEY_TO_API_KEY,
} from "../../../constants/global.constants";
import { extractPlantId } from "../../../utils/Utils";
import { removeItemSession, saveItemSession } from "../../../utils/session";
import { FIELD_KEYS, getInitialValues } from "../utilityCosts.config";
import createNotification, {
  ERROR_NOTIFICATION,
  SUCCESS_NOTIFICATION,
} from "../../Notifications";

const normalizeUtilityCostsResponse = (apiData) => {
  if (!apiData) {
    return {};
  }

  if (Array.isArray(apiData)) {
    return apiData.reduce((acc, item) => {
      const utilityType = item?.utility_type;
      const value = item?.price_per_unit;

      if (utilityType) {
        acc[String(utilityType).trim().toLowerCase()] = value;
      }

      return acc;
    }, {});
  }

  return {
    ...apiData,
    water_price: apiData?.water_price ?? apiData?.water_cost,
    downtime_cost: apiData?.downtime_cost,
    energy_price: apiData?.energy_price ?? apiData?.energy_cost,
  };
};

const toNumberValue = (value) => {
  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

export const toUtilityFormValues = (apiData = {}) => {
  const normalizedData = normalizeUtilityCostsResponse(apiData);
  const defaultValues = getInitialValues();

  return FIELD_KEYS.reduce((acc, fieldKey) => {
    const apiKey = UTILITY_FIELD_KEY_TO_API_KEY[fieldKey];
    const value = normalizedData?.[apiKey];

    acc[fieldKey] =
      value === null || value === undefined || value === ""
        ? defaultValues[fieldKey]
        : String(value);

    return acc;
  }, {});
};

export const toUtilityApiPayload = (formValues = {}) => {
  return FIELD_KEYS.reduce((acc, fieldKey) => {
    const apiKey = UTILITY_FIELD_KEY_TO_API_KEY[fieldKey];
    const rawValue = formValues[fieldKey];
    acc[apiKey] = toNumberValue(rawValue);

    return acc;
  }, {});
};

const toUtilityPatchPayload = (currentValues = {}, previousValues = null) => {
  const currentPayload = toUtilityApiPayload(currentValues);

  if (!previousValues) {
    return currentPayload;
  }

  const previousPayload = toUtilityApiPayload(previousValues);

  return Object.keys(currentPayload).reduce((acc, key) => {
    if (currentPayload[key] !== previousPayload[key]) {
      acc[key] = currentPayload[key];
    }

    return acc;
  }, {});
};

export const toStoreUtilityCosts = (apiData = {}) => {
  const normalizedData = normalizeUtilityCostsResponse(apiData);

  return {
    water_price: toNumberValue(normalizedData?.water_price),
    downtime_cost: toNumberValue(normalizedData?.downtime_cost),
    energy_price: toNumberValue(normalizedData?.energy_price),
  };
};

export const fetchUtilityCostsData = async (t, isUserScope) => {
  try {
    let response = null;
    const plantId = extractPlantId();

    if (plantId && !isUserScope) {
      response = await getPlantUtilityCosts();
    } else {
      response = await getUserUtilityCosts();
    }

    if (response?.status === 200) {
      return toUtilityFormValues(response?.data || {});
    }

    return getInitialValues();
  } catch (error) {
    createNotification(
      ERROR_NOTIFICATION,
      t("notificationStrings.somethingWrong"),
      "",
    );

    return getInitialValues();
  }
};

export const updateUtilityCostsData = async (
  values,
  t,
  isUserScope,
  isMakeDefault = false,
  previousValues = null,
) => {
  try {
    const plantId = extractPlantId();
    const shouldQueuePlantUpdateLocally =
      !isUserScope && !isMakeDefault && !plantId;
    const fullPayload = toUtilityApiPayload(values);
    const payload = isMakeDefault
      ? fullPayload
      : toUtilityPatchPayload(values, previousValues);

    if (!isMakeDefault && Object.keys(payload).length === 0) {
      return {
        response: null,
        formValues: toUtilityFormValues(fullPayload),
        savedUtilityCosts: toStoreUtilityCosts(fullPayload),
        noChanges: true,
      };
    }

    if (shouldQueuePlantUpdateLocally) {
      saveItemSession(PENDING_PLANT_UTILITY_COSTS_KEY, JSON.stringify(payload));
      createNotification(
        SUCCESS_NOTIFICATION,
        t("utilityCosts.dataUpdatedSuccessfully"),
        "",
      );

      return {
        response: null,
        formValues: toUtilityFormValues(fullPayload),
        savedUtilityCosts: toStoreUtilityCosts(fullPayload),
        deferred: true,
      };
    }

    const response =
      isUserScope || isMakeDefault
        ? await updateUserUtilityCosts(payload)
        : await updatePlantUtilityCosts(payload);

    if (response?.status !== 200) {
      throw new Error(`Utility update failed with status ${response?.status}`);
    }

    if (!isUserScope && !isMakeDefault) {
      removeItemSession(PENDING_PLANT_UTILITY_COSTS_KEY);
    }

    const successMessage = isMakeDefault
      ? t("utilityCosts.dataSetAsDefaultSuccessfully")
      : t("utilityCosts.dataUpdatedSuccessfully");

    createNotification(SUCCESS_NOTIFICATION, successMessage, "");

    return {
      response,
      formValues: toUtilityFormValues(response || fullPayload),
      savedUtilityCosts: toStoreUtilityCosts(response || fullPayload),
    };
  } catch (error) {
    createNotification(
      ERROR_NOTIFICATION,
      t("notificationStrings.somethingWrong"),
      "",
    );

    return null;
  }
};
